import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { cartLineKey, useStoreCart } from '../context/StoreCartContext';
import { useNotification } from '../components/Notification';
import { CartQuantityControls, GoldenAmount, ProductCard } from '../components/store/StoreUi';

export default function StoreProduct() {
  const { slug } = useParams();
  const { user } = useAuth();
  const { items, itemCount, addItem, updateQuantity } = useStoreCart();
  const { showNotification } = useNotification();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [balance, setBalance] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [variantId, setVariantId] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    setError('');
    api
      .get(`/store/products/${slug}`)
      .then(({ data }) => {
        setProduct(data.product);
        setRelated(data.related || []);
        setBalance(data.goldenTickets ?? null);
        const first = data.product?.variants?.find((variant) => variant.active && variant.stock > 0);
        setVariantId(first?._id || '');
        setSelectedImage(0);
        setQuantity(1);
      })
      .catch((err) => setError(err?.response?.data?.message || 'Product not found.'))
      .finally(() => setLoading(false));
  }, [slug]);

  const selectedVariant = useMemo(
    () => product?.variants?.find((variant) => variant._id === variantId) || null,
    [product, variantId]
  );
  const available = product?.trackInventory
    ? selectedVariant
      ? selectedVariant.stock || 0
      : product?.stock || 0
    : 25;
  const soldOut = product && product.trackInventory && available <= 0;
  const selectedLineKey = product ? cartLineKey(product._id, selectedVariant?._id) : '';
  const cartItem = items.find((item) => item.key === selectedLineKey);
  const cartQuantity = cartItem?.quantity || 0;
  const total = (product?.priceGolden || 0) * (cartQuantity || quantity);
  const shortfall = balance == null ? 0 : Math.max(0, total - balance);
  const maxCartQuantity = product?.trackInventory ? Math.max(0, Math.min(25, available)) : 25;
  const atMaximum = cartQuantity >= maxCartQuantity;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#090d16] py-16">
        <div className="mx-auto max-w-6xl px-4">
          <div className="h-[32rem] animate-pulse rounded-3xl bg-slate-200 dark:bg-slate-800" />
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#090d16] px-4 py-20 text-center">
        <p className="text-5xl" aria-hidden>🛍️</p>
        <h1 className="mt-4 text-2xl font-black text-slate-900 dark:text-white">{error || 'Product not found'}</h1>
        <Link to="/store" className="mt-5 inline-flex rounded-xl bg-amber-400 px-5 py-3 font-bold text-slate-950">
          Back to store
        </Link>
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-slate-50 dark:bg-[#090d16] py-8 sm:py-10">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <nav className="mb-5 flex items-center justify-between gap-3 text-sm text-slate-500 dark:text-slate-400">
          <div className="min-w-0 truncate">
            <Link to="/store" className="hover:text-amber-600">Store</Link>
            <span className="mx-2">/</span>
            <span>{product.category}</span>
          </div>
          <Link
            to="/store/cart"
            className="relative inline-flex shrink-0 items-center gap-2 rounded-xl bg-slate-950 dark:bg-white px-4 py-2.5 font-bold text-white dark:text-slate-950 hover:opacity-90"
          >
            <span aria-hidden>🛒</span>
            View cart
            {itemCount > 0 && (
              <span className="rounded-full bg-amber-400 px-2 py-0.5 text-xs font-black text-slate-950">
                {itemCount}
              </span>
            )}
          </Link>
        </nav>

        <div className="grid gap-8 lg:grid-cols-2">
          <section>
            <div className="aspect-square overflow-hidden rounded-3xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900">
              {product.images?.[selectedImage]?.url ? (
                <img
                  src={product.images[selectedImage].url}
                  alt={product.images[selectedImage].alt || product.name}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full items-center justify-center text-8xl" aria-hidden>🎁</div>
              )}
            </div>
            {product.images?.length > 1 && (
              <div className="mt-3 grid grid-cols-5 gap-3">
                {product.images.map((image, index) => (
                  <button
                    key={`${image.url}-${index}`}
                    onClick={() => setSelectedImage(index)}
                    className={`aspect-square overflow-hidden rounded-xl border-2 ${
                      selectedImage === index ? 'border-amber-500' : 'border-transparent'
                    }`}
                  >
                    <img src={image.url} alt={image.alt || `${product.name} ${index + 1}`} className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </section>

          <section className="lg:py-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-amber-100 dark:bg-amber-500/15 px-3 py-1 text-xs font-black uppercase tracking-wide text-amber-800 dark:text-amber-300">
                {product.category}
              </span>
              {product.brand && <span className="text-sm text-slate-500 dark:text-slate-400">{product.brand}</span>}
            </div>
            <h1 className="mt-4 text-3xl sm:text-4xl font-black text-slate-950 dark:text-white">{product.name}</h1>
            <div className="mt-4 flex items-center gap-3">
              <GoldenAmount amount={product.priceGolden} size="xl" />
              {product.compareAtGolden > product.priceGolden && (
                <span className="text-lg text-slate-400 line-through">{product.compareAtGolden}</span>
              )}
            </div>
            <p className="mt-5 text-base leading-7 text-slate-600 dark:text-slate-300">
              {product.shortDescription || product.description}
            </p>

            {product.variants?.length > 0 && (
              <div className="mt-7">
                <label htmlFor="store-variant" className="mb-2 block text-sm font-bold text-slate-800 dark:text-slate-200">
                  Select option
                </label>
                <select
                  id="store-variant"
                  value={variantId}
                  onChange={(event) => {
                    setVariantId(event.target.value);
                    setQuantity(1);
                  }}
                  className="w-full rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900 px-4 py-3 text-slate-900 dark:text-white"
                >
                  <option value="">Choose an option</option>
                  {product.variants.filter((variant) => variant.active).map((variant) => (
                    <option key={variant._id} value={variant._id} disabled={product.trackInventory && variant.stock <= 0}>
                      {variant.name}{product.trackInventory ? ` — ${variant.stock > 0 ? `${variant.stock} left` : 'sold out'}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {!cartItem && (
              <div className="mt-5 flex items-end gap-3">
                <label className="block">
                  <span className="mb-2 block text-sm font-bold text-slate-800 dark:text-slate-200">Quantity</span>
                  <select
                    value={quantity}
                    onChange={(event) => setQuantity(Number(event.target.value))}
                    disabled={soldOut || (product.variants?.length && !selectedVariant)}
                    className="rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900 px-4 py-3 text-slate-900 dark:text-white"
                  >
                    {Array.from({ length: Math.max(1, Math.min(25, available || 1)) }).map((_, index) => (
                      <option key={index + 1} value={index + 1}>{index + 1}</option>
                    ))}
                  </select>
                </label>
                <div className="flex-1 rounded-xl bg-slate-100 dark:bg-white/5 px-4 py-3">
                  <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Item total</p>
                  <GoldenAmount amount={total} size="lg" />
                </div>
              </div>
            )}

            {user && balance != null && (
              <div className={`mt-5 rounded-xl border p-4 ${
                shortfall > 0
                  ? 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/20'
                  : 'border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/20'
              }`}>
                <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                  Your balance: <GoldenAmount amount={balance} size="sm" />
                </p>
                {shortfall > 0 && (
                  <p className="mt-1 text-sm text-red-700 dark:text-red-300">You need {shortfall} more golden ticket{shortfall === 1 ? '' : 's'}.</p>
                )}
              </div>
            )}

            {cartItem ? (
              <div className="mt-5 rounded-2xl border border-amber-200 dark:border-amber-500/30 bg-white dark:bg-slate-900 p-3 shadow-sm">
                <div className="mb-3 flex items-center justify-between gap-3 px-1">
                  <div>
                    <p className="text-xs font-black uppercase tracking-wide text-emerald-700 dark:text-emerald-400">
                      In your cart
                    </p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      Adjust this {selectedVariant?.name ? `${selectedVariant.name} ` : ''}item quantity.
                    </p>
                  </div>
                  <Link
                    to="/store/cart"
                    className="shrink-0 rounded-xl bg-slate-950 dark:bg-white px-4 py-2.5 text-sm font-bold text-white dark:text-slate-950 hover:opacity-90"
                  >
                    Checkout cart →
                  </Link>
                </div>
                <CartQuantityControls
                  quantity={cartItem.quantity}
                  onDecrease={() => updateQuantity(cartItem.key, cartItem.quantity - 1)}
                  onIncrease={() => addItem(product, selectedVariant, 1)}
                  disableIncrease={atMaximum}
                  className="w-full"
                />
              </div>
            ) : (
              <button
                disabled={soldOut || (product.variants?.length && !selectedVariant)}
                onClick={() => {
                  addItem(product, selectedVariant, quantity);
                  showNotification(`${product.name} added to cart`, 'success');
                }}
                className="mt-5 w-full rounded-2xl bg-gradient-to-r from-amber-400 to-orange-500 px-6 py-4 text-base font-black text-slate-950 shadow-lg shadow-amber-500/20 hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {soldOut ? 'Sold out' : product.variants?.length && !selectedVariant ? 'Choose an option' : 'Add to cart'}
              </button>
            )}

            <div className="mt-6 grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-xl border border-slate-200 dark:border-white/10 p-3 text-slate-600 dark:text-slate-300">
                <span className="mr-2" aria-hidden>🔒</span> Secure ticket checkout
              </div>
              <div className="rounded-xl border border-slate-200 dark:border-white/10 p-3 text-slate-600 dark:text-slate-300">
                <span className="mr-2" aria-hidden>📦</span> ~{product.estimatedDeliveryDays} day delivery
              </div>
            </div>

            {product.description && product.description !== product.shortDescription && (
              <div className="mt-8 border-t border-slate-200 dark:border-white/10 pt-6">
                <h2 className="font-bold text-slate-900 dark:text-white">Product details</h2>
                <p className="mt-2 whitespace-pre-line text-sm leading-7 text-slate-600 dark:text-slate-300">{product.description}</p>
              </div>
            )}
          </section>
        </div>

        {related.length > 0 && (
          <section className="mt-16">
            <h2 className="mb-5 text-2xl font-black text-slate-900 dark:text-white">More from this category</h2>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {related.map((item) => <ProductCard key={item._id} product={item} />)}
            </div>
          </section>
        )}
      </div>
    </main>
  );
}
